// V-4 — A quién se puede poner al frente de un grupo o de una sesión.
//
// El club tiene un grupo real, «Alevin A», activo y SIN entrenador asignado, con
// 12 sesiones por delante. No es un descuido: quien lo lleva es un director, y
// hasta ahora el desplegable de «Entrenador» solo ofrecía `role = 'monitor'`, así
// que no había a quién elegir. Lo medido el día de escribir esto: 7 directores, y
// ninguno aparece como entrenador de ningún grupo, sesión ni clase especial —
// porque el dato no lo podía representar.
//
// ⚠️ ESTO ES SOLO EL LADO DE LA ASIGNACIÓN, a propósito. `profiles.role` sigue
// siendo una columna con cinco valores y un director sigue siendo director: no se
// toca su rol, ni la RLS, ni nada de lo que ve.
//
// LA FRONTERA ES «ASIGNACIÓN» CONTRA «PERSONAL», y la primera versión de esta
// nota la trazó mal. Decía que solo Grupos y Sesiones se ensanchaban y que las
// demás consultas con `role = 'monitor'` pintan gestión de personal. Tres de
// ellas no: elegir quién FIRMA un informe, quién lleva la preparación física de
// alguien y quién analiza un vídeo son asignaciones, igual que elegir quién
// lleva un grupo. Se vio cuando un director —Sergio Blesa, que lleva Alevin A—
// no aparecía en el desplegable de Evaluaciones. Ahora son NUEVE desplegables:
//
//   asignación (se ensanchan) → Grupos, Sesiones (×5), Evaluaciones, el pool de
//                               Preparación Física y el de Análisis de Vídeo
//   personal   (NO se ensanchan) → la lista de Entrenadores, su detalle y el
//                               panel de avisos: ahí un director no pinta nada
//
// Los dos pools conservan su flag (`is_physical_trainer`,
// `can_do_video_analysis`): se ensancha el rol, no el requisito.
//
// ⚠️ Y una consulta que NO es un desplegable pero va con ellos: la que resuelve
// el NOMBRE del analista ya asignado en Vídeo. Ensanchar el pool sin ensanchar
// ésa deja un hueco donde iba el nombre, y parece un fallo de datos.
//
// La otra mitad —que un director pueda MIRAR el club desde la vista de
// entrenador— es el conmutador (V-1/V-2). Sin esta pieza, aquélla no tendría a
// dónde llevar: la bandera `llevaSesiones` de `app/src/lib/vistas.ts` se deriva
// justamente de que alguien te haya apuntado aquí.
//
// Y la base no estorba: `groups.monitor_id` y `sessions.monitor_id` son FKs a
// `profiles` SIN ningún CHECK de rol. Lo que lo impedía era la pantalla.

/** Los roles que pueden quedar al frente de un grupo o una sesión. */
export const ROLES_ASIGNABLES = ['monitor', 'director'] as const;

/**
 * Un candidato del desplegable.
 *
 * `role` es opcional porque lo es en la práctica: hay consultas que no lo piden
 * y embeds que no lo traen. Sin rol se trata como entrenador, que es el caso
 * común y el que NO cambia de sitio en la lista — solo un `'director'` explícito
 * baja al grupo de Dirección.
 */
export interface EntrenadorAsignable {
  id: string;
  full_name: string;
  role?: string | null;
}

/**
 * Parte la lista en los dos grupos del desplegable, conservando el orden de
 * entrada (las consultas ya vienen ordenadas por nombre).
 *
 * Dirección va DEBAJO y con su rótulo. Mezclarlos sin distinguir convertiría un
 * desplegable de 4 nombres en uno de 11 donde no se sabe quién es quién; y
 * ponerlos arriba pondría el caso raro delante del habitual.
 */
export function agrupaEntrenadores(lista: readonly EntrenadorAsignable[]): {
  deEquipo: EntrenadorAsignable[];
  deDireccion: EntrenadorAsignable[];
} {
  return {
    deEquipo: lista.filter((p) => p.role !== 'director'),
    deDireccion: lista.filter((p) => p.role === 'director'),
  };
}
